/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {
    LocalStorage,
    BrowserUtils,
    Logger,
    IPerformanceClient,
} from "@azure/msal-browser";
import type {
    ApplicationInsights,
    IExtendedConfiguration,
    IExtendedTelemetryItem,
} from "@microsoft/1ds-analytics-js";
import {
    PerformanceEvent,
    PerformanceEvents,
    TelemetryQueueConfig,
} from "./TelemetryUtils.js";

// Instrumentation Key for "MSALJS Tenant" Cluster
const instrumentationKey =
    "d634483c08244c1ca09af2b2d952c92e-ab2bba03-2ba3-49d8-a82c-ef6da750d8ab-7725";
const profileIdCacheKey = `mats-telemetry-profile-id`;
const loggerPrefix = "PerformanceTelemetryReporter:";
const attributeMapping: Map<string, string> = new Map([
    ["profileTelemetryId", "profileTelemetryIdV2"],
    ["sessionId", "sessionIdV2"],
    ["correlationId", "correlationIdV2"],
    ["requestId", "requestIdV2"],
]);

/**
 * Returns boolean flag, indicating whether additional observability should be added to event
 */
export function isObservableEvent(ratio: number): boolean {
    const randomNumber = Math.random();
    return randomNumber <= ratio;
}

// Amortized O(1) two stack queue
function amortizedQueue() {
    const inbound: IExtendedTelemetryItem[] = [];
    const outbound: IExtendedTelemetryItem[] = [];

    const enqueue = (value: IExtendedTelemetryItem) => {
        inbound.push(value);
    };

    const dequeue = () => {
        if (outbound.length === 0) {
            let el = inbound.pop();
            while (el) {
                outbound.push(el);
                el = inbound.pop();
            }
        }
        return outbound.pop();
    };

    const size = () => inbound.length + outbound.length;
    const clear = () => {
        inbound.length = 0;
        outbound.length = 0;
    };

    return { enqueue, dequeue, size, clear };
}

export class PerformanceTelemetryReporter {
    private readonly clientId: string;
    private logger: Logger;
    private readonly eventsToEmit: Set<string>;
    private readonly miscEvents: Set<string>;
    private appInsights: ApplicationInsights | null = null;
    private appInsightsPromise: Promise<ApplicationInsights> | null = null;
    private readonly appInsightsConfig: IExtendedConfiguration;
    readonly synthetic: boolean = false;
    private readonly observabilityRatio = 0.1;
    private readonly samplingRatio = 0.01;
    private readonly appName?: string;
    private readonly appVersion?: string;
    private readonly profileTelemetryId: string;
    private readonly channelId: string;
    private readonly sampleEvents: boolean;
    private telemetryQueue = amortizedQueue();
    private isProcessingQueue = false;
    private readonly maxQueueSize: number;
    private readonly maxRetries: number;
    private readonly onOnInitializationFailure?: (error: Error) => void;
    private initializationRetries = 0;
    private telemetryDisabled = false;

    constructor(
        clientId: string,
        logger: Logger,
        performanceClient: IPerformanceClient,
        eventsToEmit: PerformanceEvents[],
        miscEvents: PerformanceEvents[],
        channelId: string,
        endpointUrl: string,
        synthetic?: boolean,
        appName?: string,
        appVersion?: string,
        queueConfig?: TelemetryQueueConfig,
        profileTelemetryId?: string
    ) {
        this.clientId = clientId;
        this.logger = logger;
        this.eventsToEmit = new Set(eventsToEmit);
        this.miscEvents = new Set(miscEvents);

        // Configure queue management
        this.maxQueueSize = queueConfig?.maxQueueSize || 1000;
        this.maxRetries = queueConfig?.maxRetries || 3;
        this.onOnInitializationFailure = queueConfig?.onInitializationFailure;

        // Store config for lazy initialization
        this.appInsightsConfig = {
            instrumentationKey,
            extensions: [],
            propertyConfiguration: {
                gpcDataSharingOptIn: false,
            },
            disableCookiesUsage: true,
            endpointUrl,
        };

        this.synthetic = synthetic || false;
        this.appName = appName;
        this.appVersion = appVersion;
        this.channelId = channelId;
        this.sampleEvents = Math.random() <= this.samplingRatio;
        this.logger.verbose(
            `Sampling for misc event enabled: ${this.sampleEvents}`
        );
        try {
            const localstorage = new LocalStorage(
                this.clientId,
                logger,
                performanceClient
            );
            if (profileTelemetryId) {
                this.profileTelemetryId = profileTelemetryId;
                localstorage.setItem(profileIdCacheKey, profileTelemetryId);
            } else {
                const cachedProfileId = localstorage.getItem(profileIdCacheKey);
                if (cachedProfileId) {
                    this.profileTelemetryId = cachedProfileId;
                } else {
                    const guid = BrowserUtils.createGuid();
                    this.profileTelemetryId = guid;
                    localstorage.setItem(profileIdCacheKey, guid);
                }
            }
        } catch (e) {
            logger.warning(`${loggerPrefix} local storage is not available`);
            this.profileTelemetryId = "";
        }
        this.logger.verbose(`${loggerPrefix} Initialized`);
    }

    /**
     * Lazy initialization of ApplicationInsights with singleton pattern
     * Ensures only one initialization happens even with concurrent calls
     */
    async initializeAppInsights(): Promise<ApplicationInsights> {
        if (this.appInsights !== null) {
            return this.appInsights;
        }

        // If initialization is already in progress, return the existing promise
        if (this.appInsightsPromise !== null) {
            return this.appInsightsPromise;
        }

        if (this.initializationRetries >= this.maxRetries) {
            this.telemetryDisabled = true;
            // we won't be able to send telemetry anymore, so clear the queue
            this.telemetryQueue.clear();
            this.logger.warning(
                `${loggerPrefix} 1DS initialization failed after ${this.maxRetries} attempts. Telemetry disabled to prevent application issues.`
            );
            this.onOnInitializationFailure?.(
                new Error(
                    `1DS initialization failed after ${this.maxRetries} attempts`
                )
            );
        }

        // Start initialization and cache the promise
        this.appInsightsPromise = this.initAnalyticsDynamically();

        try {
            this.appInsights = await this.appInsightsPromise;
            return this.appInsights;
        } catch (error) {
            // Reset promise on failure so retry is possible
            this.appInsightsPromise = null;
            this.initializationRetries++;
            this.onOnInitializationFailure?.(
                error instanceof Error ? error : new Error(String(error))
            );
            throw error;
        }
    }

    /**
     * Actual ApplicationInsights initialization logic
     */
    private async initAnalyticsDynamically(): Promise<ApplicationInsights> {
        this.logger.verbose(
            `${loggerPrefix} Lazy loading and initializing 1DS`
        );

        try {
            // Dynamic import to avoid including ApplicationInsights in the main bundle
            const { ApplicationInsights } = await import(
                "@microsoft/1ds-analytics-js"
            );
            const appInsights = new ApplicationInsights();
            appInsights.initialize(this.appInsightsConfig, []);
            this.logger.verbose(
                `${loggerPrefix}: 1DS successfully loaded and initialized`
            );
            return appInsights;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            this.logger.error(
                `${loggerPrefix} Failed to load 1DS`,
                errorMessage
            );
            throw error;
        }
    }

    sendCustomEvent(event: PerformanceEvent): void {
        if (this.telemetryDisabled) {
            return;
        }

        if (!this.eventsToEmit.has(event.name)) {
            this.logger.verbose(
                `${loggerPrefix} Skipping telemetry for event ${event.name}`,
                event.correlationId
            );
            return;
        }

        // Sample all network and failed events. Sample 1% of misc and cache events.
        if (
            event.success &&
            event.name !== PerformanceEvents.LoadExternalTokens &&
            !this.sampleEvents &&
            (this.miscEvents.has(event.name) || event.fromCache === true)
        ) {
            this.logger.verbose(
                `${loggerPrefix} Skipping telemetry for event ${event.name}`,
                event.correlationId
            );
            return;
        }

        // Check queue size before adding new events
        if (this.telemetryQueue.size() >= this.maxQueueSize) {
            this.logger.warning(
                `${loggerPrefix} Queue size limit (${this.maxQueueSize}) reached. Dropping telemetry event ${event.name}.`
            );
            return;
        }

        const dataToEmit: PerformanceEvent = {
            ...event,
            synthetic: this.synthetic, // Synthetic events used for benchmarking purposes
            appName: this.appName,
            appVersion: this.appVersion,
            profileTelemetryId: this.profileTelemetryId,
            sessionId: this.channelId,
        };

        // remove additional observability data for 90% of successful events
        if (event.success && !isObservableEvent(this.observabilityRatio)) {
            delete dataToEmit.context;
            delete dataToEmit.errorStack;
            this.logger.verbose(
                `${loggerPrefix} Sending ${dataToEmit.name} for tracking`
            );
        } else {
            this.logger.verbose(
                `${loggerPrefix} Sending ${dataToEmit.name} with additional observability for tracking`
            );
        }

        // Update attribute names by adding a v2 suffix. Old keys (w/o a suffix) will be discarded by Aria pipeline later to comply with EUDB.
        const extendedTelemetryData = dataToEmit as Record<string, unknown>;
        for (const [key, value] of attributeMapping.entries()) {
            if (extendedTelemetryData.hasOwnProperty(key)) {
                extendedTelemetryData[value] = extendedTelemetryData[key];
                delete extendedTelemetryData[key];
            }
        }

        // Add to queue to ensure proper ordering
        this.telemetryQueue.enqueue({
            name: event.name,
            data: extendedTelemetryData as IExtendedTelemetryItem["data"],
        });

        // Fire and forget - process queue asynchronously without blocking the caller
        this.processQueueAsync().catch((error) => {
            // Gracefully handle telemetry failures to avoid breaking the main application flow
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            this.logger.warning(
                `${loggerPrefix} Failed to send telemetry for event ${event.name}: ${errorMessage}`
            );
        });
    }

    /**
     * Process the telemetry queue to ensure events are sent in order
     */
    private async processQueueAsync(): Promise<void> {
        // Prevent concurrent queue processing
        if (this.isProcessingQueue) {
            return;
        }

        this.isProcessingQueue = true;

        try {
            // Initialize ApplicationInsights once for all queued events
            const appInsights = await this.initializeAppInsights();

            // Process all queued events in order
            while (this.telemetryQueue.size() > 0) {
                const nextEvent = this.telemetryQueue.dequeue();
                nextEvent && this.sendSingleEvent(appInsights, nextEvent);
            }
        } finally {
            this.isProcessingQueue = false;
        }
    }

    /**
     * Send a single telemetry event
     */
    private sendSingleEvent(
        appInsights: ApplicationInsights,
        event: IExtendedTelemetryItem
    ): void {
        appInsights.track(event);
        if (event.name === PerformanceEvents.AcquireTokenPreRedirect) {
            appInsights.flush();
        }
    }
}
